import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';

import { FlagGroup } from '@atlaskit/flag/flag-group';

import CheckCircleIcon from '@atlaskit/icon/core/status-success';

import { token } from '@atlaskit/tokens';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { useIsWacContext } from '@post-office/browser--growth/common/index';

import { useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { WacToast } from './wac-toast';

import { removeMessage } from '../utils/optimisticUpdateHelpers';

import { type JSX } from 'react/jsx-runtime';

type ProductDismissalFlagProps = {
	dismissedMessageIds: string[];
	handleDismissFlag: (messageId: string) => void;
};

// /**--Title--**/

const Title = () => (
	<FormattedMessage
		id="growth.app-switcher-discovery-section.flag.title"
		defaultMessage="Got it, thanks for letting us know"
		description="The title for a flag which is displayed when the Not Interested button is pressed."
	/>
);

// /**--Description--**/

const Description = () => (
	<FormattedMessage
		id="growth.app-switcher-discovery-section.flag.description"
		defaultMessage="You won’t see this here as often."
		description="The subtitle for a flag which is displayed when the Not Interested button is pressed."
	/>
);

export const ProductDismissalFlag = ({
	dismissedMessageIds,
	handleDismissFlag,
}: ProductDismissalFlagProps): JSX.Element => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const isInWacContext = useIsWacContext();

	const onDismissed = (messageId: string) => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'adControlsFlagClose',
		});
		handleDismissFlag(messageId);
	};

	return (
		<FlagGroup>
			{dismissedMessageIds.map((messageId) =>
				isInWacContext ? (
					<WacToast
						key={messageId}
						title={<Title />}
						description={<Description />}
						onDismissed={() => onDismissed(messageId)}
					/>
				) : (
					<AutoDismissFlag
						icon={
							<CheckCircleIcon
								spacing="spacious"
								color={token('color.icon.success', '#00875A')}
								label="Success"
							/>
						}
						description={<Description />}
						id={messageId}
						key={messageId}
						title={<Title />}
						appearance={'normal'}
						onDismissed={() => onDismissed(messageId)}
					/>
				),
			)}
		</FlagGroup>
	);
};

type UseProductDismissal = {
	dismissedMessageIds: string[];
	showFlag: (dismissedMessageId: string) => void;
	handleDismissFlag: (dismissedMessageId: string) => void;
};

export const useProductDismissalFlag = (): UseProductDismissal => {
	const [dismissedMessageIds, setDismissedMessageIds] = useState<string[]>([]);

	return {
		dismissedMessageIds,
		showFlag: (dismissedMessageId) =>
			setDismissedMessageIds((current) => [...current, dismissedMessageId]),
		handleDismissFlag: (dismissedMessageId: string) =>
			setDismissedMessageIds(removeMessage(dismissedMessageId)),
	};
};
